/**
 * The animal mind — M2-05's whole argument (AoD §7.6, applied to beasts).
 *
 * Two rules shape everything here, and both are reactions to what a monster in
 * a roguelike usually is.
 *
 * **It acts through the intent API, exactly as a player does.** Nothing below
 * moves an actor by assignment or resolves a blow by calling the resolver. It
 * builds a `move` or an `attack` intent and puts it through the same
 * `check` → `apply` the wire does, so a wolf cannot walk through a wall the
 * server would have refused a player, cannot swing outside its reach, and
 * cannot escape the engagement cap. That was M2-01's stated requirement and it
 * is worth more than the tidier code: the day these two paths disagree is the
 * day a creature becomes a bug report nobody can reproduce.
 *
 * **It decides from what it can perceive, never from an aggro radius.** The
 * state machine reads `senses.ts` and a short memory, so stalking a stag,
 * flanking a wolf and approaching downwind are all things that *work* — not
 * because anybody wrote a special case for them, but because the animal
 * genuinely does not know where you are.
 *
 * The states are the ticket's: `graze → alert → stalk | flee | fight`, with
 * flight standing in for disengagement, because an animal that has decided to
 * leave and an animal that is running are not two different things.
 *
 * Morale is the interesting half. A wolf's nerve falls as it takes wounds, as
 * packmates go down and as a fight drags on, and when it runs out the wolf
 * leaves — which is why a pack probes rather than commits. A boar's nerve does
 * the opposite: `enraged` inverts the wound term, so **the wounded boar is the
 * dangerous boar**. That one flag is the seed of the whole Ysgithyrwyn fight
 * in M5, and it is a flag rather than a subclass because it should be.
 */
import { isDown } from './vitals.js';
/* -------------------------------------------------------------------------- *
 * Instinct — the species, as content
 * -------------------------------------------------------------------------- */
/** What an animal does about people it has noticed. */
export const DISPOSITIONS = [
    'predator',
    'prey',
    'territorial',
    // M5-01. The village's own animals: bred out of fear over more generations
    // than anybody counted, and the reason a pen works at all. A docile animal
    // notices you and goes on eating; it moves off only when you are on top of
    // it, and it will fight only when there is nowhere left to move to. That is
    // what makes livestock *wealth* rather than wildlife — you can walk up to it,
    // which is precisely why the wolves can too.
    'docile',
];
export const DEFAULT_INSTINCT = {
    nerve: 4,
    woundNerve: 1,
    packNerve: 1.5,
    oddsNerve: 1,
    closeAt: 1,
    flightAt: 4,
    homeRange: 10,
    memory: 50,
};
/* -------------------------------------------------------------------------- *
 * The record
 * -------------------------------------------------------------------------- */
export const BEAST_STATES = ['graze', 'alert', 'stalk', 'fight', 'flee'];
export function createMind(options) {
    return {
        instinctId: options.instinctId,
        state: 'graze',
        targetId: null,
        guardedBy: null,
        since: 0,
        seenAt: -1,
        seenX: options.x,
        seenY: options.y,
        packId: options.packId ?? null,
        homeX: options.x,
        homeY: options.y,
        // Born standing where it means to be. The first graze tick picks somewhere
        // to go, which is also what makes a fresh animal and a restored one behave
        // identically from their first tick.
        wanderX: options.x,
        wanderY: options.y,
        pauseUntil: 0,
        errand: null,
    };
}
/* -------------------------------------------------------------------------- *
 * The hours of the day (M3-10)
 * -------------------------------------------------------------------------- */
/**
 * How active an animal is at its quietest.
 *
 * Never zero, and that is the whole design of reading the clock as a multiplier
 * rather than as a spawn table: a rabbit at noon is a rabbit lying up in the
 * hedge, and a player who walks past one at noon has learned something true. An
 * animal that had been despawned would have taught them that the valley is
 * empty at noon, which is a lie the game would then have to keep telling.
 */
export const MIN_ACTIVITY = 0.25;
/** Where a crepuscular animal's day peaks, on the `daylight` scale. */
export const CREPUSCULAR_PEAK = 0.5;
/**
 * How much of its range an animal will use, given the light.
 *
 * Diurnal animals track the sun. Crepuscular ones peak on the ramps — half
 * light, which is dawn and dusk — and fall off towards both midday and
 * midnight, which is what makes *when* you go out matter and pairs exactly with
 * M3-08's "go after it now or lose the trail".
 */
export function activityFor(instinct, light) {
    const clamped = Math.max(0, Math.min(1, light));
    const shape = instinct.crepuscular
        ? 1 - Math.abs(clamped - CREPUSCULAR_PEAK) / CREPUSCULAR_PEAK
        : clamped;
    return MIN_ACTIVITY + (1 - MIN_ACTIVITY) * Math.max(0, shape);
}
/** What a fight has cost an animal so far, in levels of wound. */
export function harmTaken(bearer) {
    if (!bearer)
        return 0;
    let total = 0;
    for (const wound of bearer.vitals.wounds)
        total += wound.level;
    return total;
}
/** Ticks of fighting past which an animal starts asking what it is doing here. */
export const WEARY_TICKS = 200;
export const WEARY_NERVE = 2;
/**
 * How much fight an animal has left in it.
 *
 * Stated as a reading rather than a boolean so a test — and later the client —
 * can ask *why* something ran, which is the difference between tuning this and
 * guessing at it.
 */
/**
 * What standing on your own ground is worth (M5-03).
 *
 * Two points of nerve, which is enough to change the answer rather than to
 * settle it: a wolf that would have broken after one wound holds for a second,
 * and a pack that has already lost two still leaves. That is the shape the
 * ticket asks for — a den assault should be measurably harder than the same
 * fight in the open, and it should still be winnable by the same means.
 *
 * There are no pups in the data and there is nothing depicted. What the number
 * says is only that an animal defends the place it lives in, which is true of
 * every animal and needs no scene to justify it.
 */
export const HOME_GROUND_NERVE = 2;
export function morale(input) {
    const wounds = harmTaken(input.combatant);
    const odds = Math.max(0, input.enemies - 1);
    // The inversion. A hurt wolf wants to be somewhere else; a hurt boar wants
    // to be exactly where it is, and closer.
    const woundTerm = input.instinct.enraged
        ? +wounds * input.instinct.woundNerve
        : -wounds * input.instinct.woundNerve;
    const nerve = input.instinct.nerve +
        woundTerm -
        input.packDown * input.instinct.packNerve -
        odds * input.instinct.oddsNerve -
        (input.fightingFor >= WEARY_TICKS ? WEARY_NERVE : 0) +
        (input.atHome ? HOME_GROUND_NERVE : 0);
    return { nerve, wounds, packDown: input.packDown, odds, cornered: input.cornered };
}
export function willFight(reading) {
    return reading.cornered || reading.nerve > 0;
}
/**
 * How many of this animal's pack are dead, senseless, or have already left.
 *
 * Counted from the minds table each tick rather than kept as a tally, because a
 * tally is a second place that has to learn about every future way of a wolf
 * ceasing to be a going concern — and because a stale one would break the pack
 * exactly when it matters.
 */
export function packDownFor(world, planOf, self, packId, tick) {
    if (packId === null)
        return 0;
    let down = 0;
    for (const [id, mind] of Object.entries(world.minds)) {
        if (id === self || mind.packId !== packId)
            continue;
        const actor = world.actors[id];
        if (!actor) {
            down += 1;
            continue;
        }
        const combatant = world.combat[id];
        const plan = combatant ? planOf(combatant.loadout.planId) : null;
        if (!combatant || combatant.vitals.dead) {
            down += 1;
        }
        else if (plan && isDown(plan, combatant.vitals, tick)) {
            down += 1;
        }
        else if (mind.state === 'flee') {
            // A packmate that has broken is a packmate that is no longer beside you,
            // which is what the animal in the fight actually notices.
            down += 1;
        }
    }
    return down;
}
/**
 * The pack's shared quarry: whoever the most packmates are already on.
 *
 * This is all the coordination wolves need. Flanking is *emergent* rather than
 * scripted — M2-02's engagement cap means the third and fourth wolf are refused
 * a place at the front, so they walk round looking for one, and what that looks
 * like from inside the fight is a pack circling.
 */
export function packQuarry(world, packId) {
    if (packId === null)
        return null;
    const votes = new Map();
    for (const mind of Object.values(world.minds)) {
        if (mind.packId !== packId || mind.targetId === null)
            continue;
        if (mind.state !== 'stalk' && mind.state !== 'fight')
            continue;
        votes.set(mind.targetId, (votes.get(mind.targetId) ?? 0) + 1);
    }
    let best = null;
    let bestVotes = 0;
    // Ties broken by id, so the pack's choice cannot depend on iteration order.
    for (const [id, count] of [...votes].sort((a, b) => a[0].localeCompare(b[0]))) {
        if (count > bestVotes) {
            best = id;
            bestVotes = count;
        }
    }
    return best;
}
//# sourceMappingURL=mind.js.map